import { Component, inject, signal, ViewChild, ChangeDetectionStrategy } from "@angular/core";
import { Table, TableLazyLoadEvent, TableModule } from "@openng/optimus-ui/table";

import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { PaginatorModule } from "@openng/optimus-ui/paginator";
import { DatePipe, NgClass } from "@angular/common";

import { SelectButtonModule } from "@openng/optimus-ui/selectbutton";
import { ProgressSpinnerModule } from "@openng/optimus-ui/progressspinner";
import { TooltipModule } from "@openng/optimus-ui/tooltip";
import { SelectModule } from "@openng/optimus-ui/select";

import { ButtonComponent } from "../../shared/components/button/button.component";
import { PAGINATOR_CONSTANTS } from "../../constants/paginator";
import { HasPermissionDirective } from "../../shared/directives/has-permission/has-permission.directive";
import { PermissionService } from "../../services/api/permission/permission.service";
import { NotificationsApiService } from "../../services/api/notifications-api/notifications-api.service";
import { first } from "rxjs";
import { createTimeoutSignal } from "../../shared/utils/timeout.utils";
import { TimeoutComponent } from "../../shared/components/timeout/timeout.component";
import { Column } from "../../models/ui/tableColumn";
import { DropdownOption } from "../../models/ui/dropdownOption";
import { FormsModule } from "@angular/forms";
import { CursorPagination } from "../../models/api/pagination";
import { Notification } from "../../models/api/notification/notification";
import { NotificationsStateService } from "../../services/state/notifications-state/notifications-state.service";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { NotificationCategory } from "../../enums/notificationCategories.enum";
import { createNotificationPresentation, NotificationPresentation } from "./notification-presentation";

@Component({
  standalone: true,
  selector: "app-notifications",
  imports: [
    TableModule,
    TranslateModule,
    PaginatorModule,
    SelectButtonModule,
    SelectModule,
    ProgressSpinnerModule,
    TooltipModule,
    ButtonComponent,
    HasPermissionDirective,
    NgClass,
    TimeoutComponent,
    FormsModule,
    DatePipe,
  ],
  templateUrl: "./notifications.component.html",
  styleUrl: "./notifications.component.scss",
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [],
})
export class NotificationsComponent {
  public paginatorOptions = PAGINATOR_CONSTANTS;
  public COLUMNS: Column[] = [
    {
      field: "read",
      header: "",
    },
    {
      field: "createdAt",
      header: "notifications.table.created_at",
    },
    {
      field: "notificationCategory",
      header: "notifications.table.notification_category",
    },
    {
      field: "notificationType",
      header: "notifications.table.notification_type",
    },
    {
      field: "notificationText",
      header: "notifications.table.notification_text",
    },
    {
      field: "reference",
      header: "notifications.table.reference",
    },
  ];
  public totalNotificationRecords = 0;
  public translate = inject(TranslateService);
  public permissions = inject(PermissionService);

  private notificationsApi = inject(NotificationsApiService);
  private notificationsState = inject(NotificationsStateService);

  public onlyUnread = false;
  public selectedCategory: NotificationCategory | "" = "";
  public categoryOptions: DropdownOption[] = [
    {
      label: "notifications.table.all_categories",
      value: "",
    },
  ];
  public switchOptions: DropdownOption[] = [
    {
      label: "notifications.table.all",
      value: false,
    },
    {
      label: "notifications.table.onlyUnread",
      value: true,
    },
  ];

  public loading = signal(false);
  public loadingMore = signal(false);
  public timeOut = createTimeoutSignal(this.loading);
  private initialLoadComplete = false;
  private skipNextInitialLazyLoad = false;

  public filteredNotifications = signal<(NotificationPresentation | undefined)[]>([]);

  @ViewChild(Table) table: Table | undefined;
  #pagination: CursorPagination = {
    pageSize: this.paginatorOptions.DEFAULT_ROWS,
  };

  constructor() {
    this.loadNewNotifications();
    this.loadNotificationCategories();
    this.notificationsState.realtimeNotification.pipe(takeUntilDestroyed()).subscribe({
      next: (notification) => {
        if (this.selectedCategory === "" || notification.category === this.selectedCategory) {
          this.filteredNotifications.update((notifications) => {
            notifications.unshift(createNotificationPresentation(notification));
            return [...notifications];
          });
          this.totalNotificationRecords += 1;
        }
      },
    });
  }

  loadNotificationCategories(): void {
    this.notificationsApi
      .getNotificationCategories()
      .pipe(first())
      .subscribe({
        next: (categories) => {
          this.categoryOptions = [
            {
              label: "notifications.table.all_categories",
              value: "",
            },
            ...categories.map((category) => ({
              label: `notifications.notification_templates.categories.${category}`,
              value: category,
            })),
          ];
        },
      });
  }

  loadNewNotifications(): void {
    this.loading.set(true);
    this.initialLoadComplete = false;
    delete this.#pagination.cursor;

    this.notificationsApi
      .getNotifications(this.onlyUnread, this.selectedCategory || undefined, this.#pagination)
      .pipe(first())
      .subscribe({
        next: (notifications) => {
          this.totalNotificationRecords = notifications.total;
          const newData = Array<NotificationPresentation | undefined>(this.totalNotificationRecords);
          newData.splice(0, this.#pagination.pageSize, ...notifications.items.map(createNotificationPresentation));
          this.filteredNotifications.set(newData);

          if (this.table && this.table.scroller) {
            this.table.scroller.lazyLoadState = {
              first: 0,
              last: this.#pagination.pageSize,
            };
          }
          this.skipNextInitialLazyLoad = true;
          this.initialLoadComplete = true;
          this.loading.set(false);
        },
        error: () => {
          this.filteredNotifications.set([]);
        },
      });
  }

  public onSwitchChange() {
    this.loadNewNotifications();
  }

  public onCategoryChange() {
    this.loadNewNotifications();
  }

  public loadMoreNotifications(event: TableLazyLoadEvent) {
    if (!this.initialLoadComplete || this.loading()) {
      return;
    }

    if (this.skipNextInitialLazyLoad && (event.first ?? 0) === 0) {
      this.skipNextInitialLazyLoad = false;
      return;
    }
    this.skipNextInitialLazyLoad = false;

    this.loadingMore.set(true);
    const notificationCursor = this.filteredNotifications()?.[(event.first ?? 1) - 1];
    if (notificationCursor) {
      this.#pagination.cursor = `${notificationCursor.notification.id}__${notificationCursor.notification.createdAt}`;
    }

    this.notificationsApi
      .getNotifications(this.onlyUnread, this.selectedCategory || undefined, this.#pagination)
      .pipe(first())
      .subscribe({
        next: (notifications) => {
          const newNotifications = notifications.items.map(createNotificationPresentation);
          this.filteredNotifications.update((data) => {
            data.splice(event.first ?? 0, newNotifications.length, ...newNotifications);
            this.loadingMore.set(false);
            return [...data];
          });
        },
        error: () => {
          this.filteredNotifications.set([]);
        },
      });
  }

  /**
   * Tell Angulars shadow DOM to track by notification id, which is unique in this dataset
   * @param _
   * @param {Notification} notification
   */
  public trackByNotificationId(_: number, presentation: NotificationPresentation | undefined) {
    return presentation?.notification.id;
  }

  public readAll() {
    this.notificationsApi.readAllNotifications().subscribe({
      next: () => {
        this.notificationsState.markAsRead(true);
        this.filteredNotifications.update((notifications) => {
          notifications.forEach((n) => {
            if (n?.notification.readStatus !== undefined) n.notification.readStatus = true;
          });
          return [...notifications];
        });
      },
    });
  }

  public readNotification(readNotification: Notification) {
    if (this.permissions.hasPermission(this.permissions.allPermissions.NOTIFICATION_UPDATE)) {
      this.notificationsApi.readSpecificNotification(readNotification.id).subscribe({
        next: () => {
          readNotification.readStatus = true;
          this.notificationsState.markAsRead(false);
          if (this.onlyUnread && this.filteredNotifications()) {
            this.filteredNotifications.update((notifications) => {
              return notifications.filter((notification) => notification?.notification.id !== readNotification.id);
            });
          }
        },
      });
    }
  }
  /**
   * Translates a given parameter using the translation service.
   * @param param The parameter key to be translated.
   * @returns The translated string associated with the parameter key.
   */
  public translateParameter(param: string | null) {
    return param ? this.translate.instant(`notifications.notification_templates.values.${param}`) : "";
  }
}
